import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { AlertTriangle, CheckCircle, Plus, Edit2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CFD_INSTRUMENTS } from '../constants/tradingPairs';
import { useMarketData } from '../contexts/MarketDataContext';
import { getInstrumentTypeFromSymbol } from '../constants/tradingTiers';
import { calculateSpreadCost, formatSpreadDisplay } from '../constants/spreadConfig';
import TakeProfitStopLossModal from './TakeProfitStopLossModal';
import { getListedCfdInstrument } from '../../supabase/functions/_shared/cfdInstruments';

interface CFDTradingFormsProps {
  usdtBalance: number;
  availableBalance?: number;
  maxForexLeverage: number;
  maxCommoditiesLeverage: number;
  maxStocksLeverage: number;
  minForexLeverage?: number;
  minCommoditiesLeverage?: number;
  minStocksLeverage?: number;
  selectedPair: string;
  currentPrice: number;
  userCfdTier?: string;
  calculateLiquidationPrice: (
    side: 'long' | 'short',
    entryPrice: number,
    leverage: number,
    marginType: 'isolated' | 'cross',
    amount?: number,
    totalBalance?: number
  ) => number;
  onCFDTrade: (
    symbol: string,
    side: 'long' | 'short',
    amount: number,
    leverage: number,
    marginType: 'isolated' | 'cross',
    stopLoss?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    takeProfit?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    orderType?: 'market' | 'limit',
    price?: number
  ) => void;
}

const CFDTradingForms: React.FC<CFDTradingFormsProps> = ({
  usdtBalance,
  availableBalance,
  maxForexLeverage,
  maxCommoditiesLeverage,
  maxStocksLeverage,
  minForexLeverage = 1,
  minCommoditiesLeverage = 1,
  minStocksLeverage = 1,
  selectedPair,
  currentPrice,
  userCfdTier,
  calculateLiquidationPrice,
  onCFDTrade
}) => {
  const { t } = useTranslation();
  const { getSnapshotPriceBySymbol, refreshSnapshot, lastSnapshotTime, isConnected: isLiveDataConnected, marketData } = useMarketData();
  const [marginType, setMarginType] = useState<'isolated' | 'cross'>('isolated');

  const instrumentType = getInstrumentTypeFromSymbol(selectedPair);
  const maxAllowedLeverage = instrumentType === 'forex' ? maxForexLeverage :
                              instrumentType === 'commodity' ? maxCommoditiesLeverage :
                              (instrumentType === 'index' || instrumentType === 'etf') ? maxStocksLeverage :
                              maxStocksLeverage;
  const minAllowedLeverage = instrumentType === 'forex' ? minForexLeverage :
                              instrumentType === 'commodity' ? minCommoditiesLeverage :
                              (instrumentType === 'index' || instrumentType === 'etf') ? minStocksLeverage :
                              minStocksLeverage;
  const isLeverageLocked = minAllowedLeverage === maxAllowedLeverage;

  const [leverage, setLeverage] = useState(Math.min(Math.max(10, minAllowedLeverage), maxAllowedLeverage));
  const [orderType, setOrderType] = useState<'limit' | 'market'>('market');
  const [inputMode, setInputMode] = useState<'lots' | 'usd'>('lots');
  const [longAmount, setLongAmount] = useState('');
  const [shortAmount, setShortAmount] = useState('');
  const [longPercentage, setLongPercentage] = useState(0);
  const [shortPercentage, setShortPercentage] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Get current price for the selected CFD instrument using snapshot
  const livePairPrice = useMemo(() => {
    // Use snapshot price for stable trading experience
    const snapshotPrice = getSnapshotPriceBySymbol(selectedPair);
    if (snapshotPrice > 0) {
      return snapshotPrice;
    }

    // Fallback to market data context
    const marketDataItem = marketData.find(data => data.symbol === selectedPair);
    if (marketDataItem && marketDataItem.price > 0) {
      return marketDataItem.price;
    }

    // Last resort: use currentPrice prop
    return currentPrice || 0;
  }, [getSnapshotPriceBySymbol, selectedPair, marketData, currentPrice]);

  useEffect(() => {
    if (isLeverageLocked) {
      setLeverage(minAllowedLeverage);
    } else if (leverage < minAllowedLeverage) {
      setLeverage(minAllowedLeverage);
    } else if (leverage > maxAllowedLeverage) {
      setLeverage(maxAllowedLeverage);
    }
  }, [minAllowedLeverage, maxAllowedLeverage, isLeverageLocked]);

const getLotSize = (symbol: string): number => {
  const listing = getListedCfdInstrument(symbol);
  if (listing) return listing.lotSize;
  const instrument = CFD_INSTRUMENTS.find(item => item.symbol === symbol);

  if (instrument && instrument.type === 'forex') {
    // Special handling for JPY pairs
    if (symbol.endsWith("JPY")) {
      return 1000; // Use 10,000 units for JPY pairs
    }
    return 100000; // Default standard forex lot size
  } else if (instrument && instrument.type === 'commodity') {
    switch (symbol) {
      case 'XAG/USD': return 5000;
      case 'XAUUSD':
      case 'XAU/USD': return 100;
      case 'XPTUSD':
      case 'XPT/USD': return 100;
      case 'XPDUSD':
      case 'XPD/USD': return 100;
      case 'NATGAS/USD': return 10000;
      case 'BCO/USD': return 1000;
      case 'WTICO/USD': return 1000;
      case 'CORN/USD': return 5000;
      case 'WHEAT/USD': return 5000;
      case 'SUGAR/USD': return 112000;
      default: return 1;
    }
  } else if (instrument && instrument.type === 'stock') {
    return 100;
  }

  return 1;
};


  // Calculate amount from percentage for long positions
  const calculateLongAmountFromPercentage = useCallback((percentage: number) => {
    if (percentage <= 0) return '';

    const priceToUse = livePairPrice;
    const lotSize = getLotSize(selectedPair);
    const balanceToUse = availableBalance !== undefined ? availableBalance : usdtBalance;
    const targetMargin = balanceToUse * (percentage / 100);
    const amount = (targetMargin * leverage) / (priceToUse * lotSize);

    // Ensure amount meets minimum trade size
    const minTradeSize = getMinTradeSize();
    const adjustedAmount = Math.max(amount, minTradeSize);

    return adjustedAmount.toFixed(getInstrumentType() === 'Stock' ? 0 : 5);
  }, [availableBalance, usdtBalance, leverage, livePairPrice, selectedPair]);

  // Calculate amount from percentage for short positions
  const calculateShortAmountFromPercentage = useCallback((percentage: number) => {
    if (percentage <= 0) return '';

    const priceToUse = livePairPrice;
    const lotSize = getLotSize(selectedPair);
    const balanceToUse = availableBalance !== undefined ? availableBalance : usdtBalance;
    const targetMargin = balanceToUse * (percentage / 100);
    const amount = (targetMargin * leverage) / (priceToUse * lotSize);

    // Ensure amount meets minimum trade size
    const minTradeSize = getMinTradeSize();
    const adjustedAmount = Math.max(amount, minTradeSize);

    return adjustedAmount.toFixed(getInstrumentType() === 'Stock' ? 0 : 5);
  }, [availableBalance, usdtBalance, leverage, livePairPrice, selectedPair]);

  // Stop Loss / Take Profit states
  const [longStopLoss, setLongStopLoss] = useState<{ trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number } | null>(null);
  const [longTakeProfit, setLongTakeProfit] = useState<{ trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number } | null>(null);
  const [shortStopLoss, setShortStopLoss] = useState<{ trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number } | null>(null);
  const [shortTakeProfit, setShortTakeProfit] = useState<{ trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number } | null>(null);

  // Modal states
  const [showLongSLModal, setShowLongSLModal] = useState(false);
  const [showLongTPModal, setShowLongTPModal] = useState(false);
  const [showShortSLModal, setShowShortSLModal] = useState(false);
  const [showShortTPModal, setShowShortTPModal] = useState(false);

  // Generate leverage options based on max allowed leverage
  const generateLeverageOptions = () => {
    const baseOptions = [1, 2, 3, 5, 10, 20, 25, 30, 50, 75, 100, 125, 150, 200, 250, 300, 400, 500, 750, 1000];
    return baseOptions.filter(option => option >= minAllowedLeverage && option <= maxAllowedLeverage);
  };

  const leverageOptions = generateLeverageOptions();
  const percentageOptions = [25, 50, 75, 100];

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case 'Starter': return '📊';
      case 'Plus': return '📈';
      case 'Advanced': return '✅';
      case 'Pro': return '🏆';
      case 'Elite': return '👑';
      default: return '📊';
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'Starter': return 'text-slate-400';
      case 'Plus': return 'text-blue-400';
      case 'Advanced': return 'text-green-400';
      case 'Pro': return 'text-amber-400';
      case 'Elite': return 'text-emerald-400';
      default: return 'text-slate-400';
    }
  };

  // Format required margin with dynamic decimal places
  const formatRequiredMargin = (value: number): string => {
    if (value === 0) return '0.00';
    if (value >= 1) return value.toFixed(2);
    if (value >= 0.01) return value.toFixed(4);
    if (value >= 0.0001) return value.toFixed(6);
    return value.toFixed(8);
  };

  // Clear error and success messages after 5 seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Real-time recalculation when leverage, price, or balance changes
  useEffect(() => {
    if (longPercentage > 0) {
      const newAmount = calculateLongAmountFromPercentage(longPercentage);
      setLongAmount(newAmount);
    }
  }, [leverage, livePairPrice, selectedPair, usdtBalance, longPercentage, calculateLongAmountFromPercentage]);

  useEffect(() => {
    if (shortPercentage > 0) {
      const newAmount = calculateShortAmountFromPercentage(shortPercentage);
      setShortAmount(newAmount);
    }
  }, [leverage, livePairPrice, selectedPair, usdtBalance, shortPercentage, calculateShortAmountFromPercentage]);

  // Get instrument type for display
  const getInstrumentType = () => {
    // Look up the instrument in CFD_INSTRUMENTS to get its type
    const instrument = CFD_INSTRUMENTS.find(item => item.symbol === selectedPair);
    if (instrument) {
      // Capitalize the first letter for display
      return instrument.type.charAt(0).toUpperCase() + instrument.type.slice(1);
    }
    
    // Default fallback
    return 'Commodity';
  };

  // Get price precision based on instrument
  const getPricePrecision = useCallback(() => {
    const isJpyPair = selectedPair.includes('JPY');
    const isForexPair = instrumentType === 'forex';

    if (isJpyPair) {
      return 3; // JPY pairs: 3 decimal places (e.g., 150.123)
    }
    if (isForexPair) {
      return 5; // Standard forex pairs: 5 decimal places (e.g., 1.12345)
    }

    switch (selectedPair) {
      case 'XAUUSD':
      case 'XAU/USD':
      case 'XAUEUR':
        return 2; // Gold: 2 decimal places (e.g., 2300.50)
      case 'XAGUSD':
      case 'XAG/USD':
      case 'XAGEUR':
        return 3; // Silver: 3 decimal places (e.g., 28.500)
      case 'XPTUSD':
      case 'XPT/USD':
      case 'XPDUSD':
      case 'XPD/USD':
        return 2; // Platinum/Palladium: 2 decimal places
      case 'WTICO/USD':
      case 'BCO/USD':
        return 3; // Oil: 3 decimal places (e.g., 75.250)
      case 'NATGAS/USD':
        return 4; // Natural Gas: 4 decimal places (e.g., 2.5000)
      case 'CORN/USD':
      case 'WHEAT/USD':
        return 4; // Grains: 4 decimal places (e.g., 600.2500)
      case 'SUGAR/USD':
        return 4; // Soft commodities: 4 decimal places (e.g., 20.5000)
      default:
        return 2; // Default: 2 decimal places for stocks/indices
    }
  }, [selectedPair, instrumentType]);

  // Get unit label for display
  const getUnitLabel = () => {
    // Use professional trading terminology - all CFD instruments are traded in lots
    return 'Lots';
  };

  // Get contract size based on instrument type
  const getContractSize = () => {
    // Contract size is always 1 for the margin calculation
    // The lot size multiplier is handled separately in getLotSize()
    return 1;
  };

  // Get minimum trade size
  const getMinTradeSize = () => {
    // Unified minimum trade size: 0.01 lots across all CFD instruments
    return 0.01; // 0.01 lots for all instruments
  };


  const convertUsdToLots = (usdAmount: number): number => {
    const lotSize = getLotSize(selectedPair);
    if (!livePairPrice || livePairPrice <= 0) return 0;
    return usdAmount / (livePairPrice * lotSize);
  };

  const convertLotsToUsd = (lotsAmount: number): number => {
    const lotSize = getLotSize(selectedPair);
    return lotsAmount * livePairPrice * lotSize;
  };

  const getLotsFromInput = (inputValue: string): number => {
    const amount = parseFloat(inputValue) || 0;
    if (inputMode === 'lots') {
      return amount;
    } else {
      return convertUsdToLots(amount);
    }
  };

  const getSpreadCost = (amount: number = 1) => {
    const lotSize = getLotSize(selectedPair);
    return calculateSpreadCost(selectedPair, livePairPrice, amount, lotSize);
  };

  const getSpreadDisplay = (amount: number = 1) => {
    const lotSize = getLotSize(selectedPair);
    return formatSpreadDisplay(selectedPair, livePairPrice, amount, lotSize);
  };

  // Calculate estimated PnL for stop loss and take profit
  const calculateEstimatedPnl = (
    side: 'long' | 'short',
    entryPrice: number,
    triggerPrice: number,
    amount: number,
    leverage: number
  ): number => {
    const lotSize = getLotSize(selectedPair);
    
    if (side === 'long') {
      // For long positions: (triggerPrice - entryPrice) * amount * lotSize
      return (triggerPrice - entryPrice) * amount * lotSize;
    } else {
      // For short positions: (entryPrice - triggerPrice) * amount * lotSize
      return (entryPrice - triggerPrice) * amount * lotSize;
    }
  };

  const handleLong = () => {
    const lotsAmount = getLotsFromInput(longAmount);
    if (!lotsAmount || lotsAmount < getMinTradeSize()) {
      setErrorMessage(`Minimum trade size is ${getMinTradeSize()} lots`);
      return;
    }

    const priceToUse = livePairPrice;
    const lotSize = getLotSize(selectedPair);
    const notionalValue = lotsAmount * priceToUse * lotSize;
    const requiredMargin = notionalValue / leverage;

    if (requiredMargin > (availableBalance !== undefined ? availableBalance : usdtBalance)) {
      setErrorMessage('Insufficient available balance');
      return;
    }

    onCFDTrade(selectedPair, 'long', lotsAmount * lotSize, leverage, marginType, longStopLoss || undefined, longTakeProfit || undefined, orderType, priceToUse);
    setSuccessMessage(`Long position opened successfully for ${lotsAmount.toFixed(5)} lots`);
    setLongAmount('');
    setLongPercentage(0);
    setLongStopLoss(null);
    setLongTakeProfit(null);
  };

  const handleShort = () => {
    const lotsAmount = getLotsFromInput(shortAmount);
    if (!lotsAmount || lotsAmount < getMinTradeSize()) {
      setErrorMessage(`Minimum trade size is ${getMinTradeSize()} lots`);
      return;
    }

    const priceToUse = livePairPrice;
    const lotSize = getLotSize(selectedPair);
    const notionalValue = lotsAmount * priceToUse * lotSize;
    const requiredMargin = notionalValue / leverage;

    if (requiredMargin > (availableBalance !== undefined ? availableBalance : usdtBalance)) {
      setErrorMessage('Insufficient available balance');
      return;
    }

    onCFDTrade(selectedPair, 'short', lotsAmount * lotSize, leverage, marginType, shortStopLoss || undefined, shortTakeProfit || undefined, orderType, priceToUse);
    setSuccessMessage(`Short position opened successfully for ${lotsAmount.toFixed(5)} lots`);
    setShortAmount('');
    setShortPercentage(0);
    setShortStopLoss(null);
    setShortTakeProfit(null);
  };

  const handleLongPercentage = (percentage: number) => {
    setLongPercentage(percentage);
    const newAmount = calculateLongAmountFromPercentage(percentage);
    setLongAmount(newAmount);
  };

  const handleShortPercentage = (percentage: number) => {
    setShortPercentage(percentage);
    const newAmount = calculateShortAmountFromPercentage(percentage);
    setShortAmount(newAmount);
  };

  const calculateLongCost = () => {
    const lotsAmount = getLotsFromInput(longAmount);
    const priceToUse = livePairPrice;
    const lotSize = getLotSize(selectedPair);
    const notionalValue = lotsAmount * priceToUse * lotSize;
    const requiredMargin = notionalValue / leverage;
    const spreadCost = lotsAmount > 0 ? getSpreadCost(lotsAmount) : 0;
    return requiredMargin + spreadCost;
  };

  const calculateShortCost = () => {
    const lotsAmount = getLotsFromInput(shortAmount);
    const priceToUse = livePairPrice;
    const lotSize = getLotSize(selectedPair);
    const notionalValue = lotsAmount * priceToUse * lotSize;
    const requiredMargin = notionalValue / leverage;
    const spreadCost = lotsAmount > 0 ? getSpreadCost(lotsAmount) : 0;
    return requiredMargin + spreadCost;
  };


  const displayInstrumentType = getInstrumentType();
  const lotSize = getLotSize(selectedPair);
  const minTradeSize = getMinTradeSize();

  const longLotsAmount = getLotsFromInput(longAmount);
  const shortLotsAmount = getLotsFromInput(shortAmount);
  const longSpreadInfo = longAmount ? getSpreadDisplay(longLotsAmount) : null;
  const shortSpreadInfo = shortAmount ? getSpreadDisplay(shortLotsAmount) : null;

  return (
    <div className="backdrop-blur-sm rounded-2xl p-3 md:p-8 border shadow-2xl overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.5)', borderColor: 'rgba(0, 0, 0, 0.06)' }}>
      {/* Status Messages */}
      {errorMessage && (
        <div className="mb-4 md:mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-3 md:p-4 flex items-center gap-2 md:gap-3">
          <AlertTriangle size={20} className="text-red-600 flex-shrink-0" />
          <span className="text-red-600">{errorMessage}</span>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 md:mb-6 bg-green-500/10 border border-green-500/30 rounded-xl p-3 md:p-4 flex items-center gap-2 md:gap-3">
          <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
          <span className="text-green-600">{successMessage}</span>
        </div>
      )}

      {/* Instrument Info */}
      <div className="mb-4 md:mb-6 rounded-xl p-3 md:p-4 border" style={{ background: 'rgba(0, 0, 0, 0.02)', borderColor: 'rgba(0, 0, 0, 0.08)' }}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-4 text-sm">
          <div>
            <span style={{ color: '#64748b' }}>Type:</span>
            <div className="font-medium" style={{ color: '#1e293b' }}>{displayInstrumentType}</div>
          </div>
          <div>
            <span style={{ color: '#64748b' }}>Lot Size:</span>
            <div className="font-medium" style={{ color: '#1e293b' }}>{lotSize.toLocaleString()}</div>
          </div>
          <div>
            <span style={{ color: '#64748b' }}>Min Size:</span>
            <div className="font-medium" style={{ color: '#1e293b' }}>{minTradeSize} lots</div>
          </div>
        </div>
      </div>

      {/* Margin Type and Leverage Controls */}
      <div className="flex flex-col gap-4 mb-4 md:mb-8">
        {/* Margin Type and Leverage Display */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex rounded-xl p-1 border" style={{ background: 'rgba(0, 0, 0, 0.03)', borderColor: 'rgba(0, 0, 0, 0.1)' }}>
            <button
              onClick={() => setMarginType('isolated')}
              className={`px-2 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 ${
                marginType === 'isolated'
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/25'
                  : 'hover:bg-slate-100'
              }`}
              style={marginType !== 'isolated' ? { color: '#64748b' } : {}}
            >
              {t('trading.isolated')}
            </button>
            <button
              onClick={() => setMarginType('cross')}
              className={`px-2 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 ${
                marginType === 'cross'
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/25'
                  : 'hover:bg-slate-100'
              }`}
              style={marginType !== 'cross' ? { color: '#64748b' } : {}}
            >
              {t('trading.cross')}
            </button>
          </div>

          <div className="font-bold text-xl sm:text-2xl bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent whitespace-nowrap">
            {leverage}x
          </div>
        </div>

        {/* Leverage Slider */}
        {isLeverageLocked ? (
          <div className="w-full">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs" style={{ color: '#64748b' }}>{t('common.leverage')}</span>
              <span className="font-bold text-sm" style={{ color: '#94a3b8' }}>{leverage}x</span>
            </div>
          </div>
        ) : (
          <div className="w-full">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs" style={{ color: '#64748b' }}>{t('common.leverage')}</span>
              <span className="font-bold text-sm" style={{ color: '#f97316' }}>{leverage}x</span>
            </div>
            <input
              type="range"
              min={minAllowedLeverage}
              max={maxAllowedLeverage}
              step="1"
              value={leverage}
              onChange={(e) => setLeverage(Number(e.target.value))}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer slider-orange"
              style={{
                background: `linear-gradient(to right, rgb(249, 115, 22) 0%, rgb(249, 115, 22) ${((leverage - minAllowedLeverage) / (maxAllowedLeverage - minAllowedLeverage || 1)) * 100}%, rgb(203, 213, 225) ${((leverage - minAllowedLeverage) / (maxAllowedLeverage - minAllowedLeverage || 1)) * 100}%, rgb(203, 213, 225) 100%)`
              }}
            />
            <div className="flex justify-between mt-1 text-xs" style={{ color: '#94a3b8' }}>
              <span>{minAllowedLeverage}x</span>
              <span>{maxAllowedLeverage}x</span>
            </div>
          </div>
        )}

        {/* Leverage Preset Buttons & Tier - Mobile Friendly */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          {!isLeverageLocked && (
            <div className="flex items-center gap-1 sm:gap-2">
              {[10, 50, 100].map((presetLeverage) => (
                presetLeverage >= minAllowedLeverage && presetLeverage <= maxAllowedLeverage && (
                  <button
                    key={presetLeverage}
                    onClick={() => setLeverage(presetLeverage)}
                    className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg font-medium text-xs sm:text-sm transition-all whitespace-nowrap ${
                      leverage === presetLeverage
                        ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30'
                        : 'border hover:bg-slate-100'
                    }`}
                    style={leverage !== presetLeverage ? { background: 'rgba(0, 0, 0, 0.02)', color: '#64748b', borderColor: 'rgba(0, 0, 0, 0.1)' } : {}}
                  >
                    {presetLeverage}x
                  </button>
                )
              ))}
            </div>
          )}

          {/* Tier Indicator - Show on all screens */}
          {userCfdTier && (
            <div className="flex border rounded-xl px-2 sm:px-4 py-1.5 sm:py-2.5 items-center gap-1 sm:gap-2" style={{ background: 'rgba(0, 0, 0, 0.02)', borderColor: 'rgba(0, 0, 0, 0.08)' }}>
              <span className="text-lg sm:text-2xl">{getTierIcon(userCfdTier)}</span>
              <div className="flex flex-col">
                <span className="text-xs" style={{ color: '#94a3b8' }}>Tier</span>
                <span className={`text-xs sm:text-sm font-bold ${getTierColor(userCfdTier)}`}>{userCfdTier}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Order Type Tabs */}
      <div className="flex items-center justify-between gap-2 sm:gap-3 md:gap-6 mb-4 md:mb-8">
        <div className="flex gap-2 sm:gap-3">
          <button
            onClick={() => setOrderType('market')}
            className={`text-xs sm:text-sm font-medium px-3 sm:px-4 py-2 rounded-lg transition-all duration-300 ${
              orderType === 'market'
                ? 'text-cyan-600 bg-cyan-400/10 border border-cyan-400/30 shadow-lg shadow-cyan-400/20'
                : 'hover:bg-slate-100'
            }`}
            style={orderType !== 'market' ? { color: '#64748b' } : {}}
          >
            {t('trading.market')}
          </button>
          <button
            onClick={() => setOrderType('limit')}
            className={`text-xs sm:text-sm font-medium px-3 sm:px-4 py-2 rounded-lg transition-all duration-300 ${
              orderType === 'limit'
                ? 'text-cyan-600 bg-cyan-400/10 border border-cyan-400/30 shadow-lg shadow-cyan-400/20'
                : 'hover:bg-slate-100'
            }`}
            style={orderType !== 'limit' ? { color: '#64748b' } : {}}
          >
            {t('trading.limit')}
          </button>
        </div>

        {/* Input Mode Toggle */}
        <div className="flex rounded-lg p-0.5 border" style={{ background: 'rgba(0, 0, 0, 0.03)', borderColor: 'rgba(0, 0, 0, 0.1)' }}>
          <button
            onClick={() => setInputMode('lots')}
            className={`px-2 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
              inputMode === 'lots'
                ? 'bg-white text-slate-800 shadow-sm'
                : ''
            }`}
            style={inputMode !== 'lots' ? { color: '#64748b' } : {}}
          >
            Lots
          </button>
          <button
            onClick={() => setInputMode('usd')}
            className={`px-2 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
              inputMode === 'usd'
                ? 'bg-white text-slate-800 shadow-sm'
                : ''
            }`}
            style={inputMode !== 'usd' ? { color: '#64748b' } : {}}
          >
            USD
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-8">
        {/* Long Form */}
        <div className="flex-1">
          <div className="mb-4 sm:mb-6">
            <div className="flex justify-between text-xs sm:text-sm mb-3" style={{ color: '#64748b' }}>
              <span>{t('trading.availableMargin')}</span>
              <span className="flex items-center gap-1.5 sm:gap-2 font-mono text-xs sm:text-sm" style={{ color: '#d91222' }}>
                {(availableBalance !== undefined ? availableBalance : usdtBalance).toFixed(4)} USD
                <div className="w-1 h-1 rounded-full animate-pulse" style={{ backgroundColor: '#d91222' }}></div>
              </span>
            </div>
          </div>

          <div className="space-y-6">
            {/* Price Input */}
            <div className="mb-3">
              <label className="block text-xs sm:text-sm mb-1 md:mb-3" style={{ color: '#64748b' }}>{t('common.price')} (USD)</label>
              <input
                type="text"
                value={livePairPrice.toFixed(getPricePrecision())}
                className="w-full bg-transparent px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all font-mono text-sm sm:text-base"
                style={{ color: '#1e293b', borderColor: 'rgba(0, 0, 0, 0.1)' }}
                readOnly
              />
            </div>

            {/* Amount Input */}
            <div className="mb-3">
              <label className="block text-xs sm:text-sm mb-1 md:mb-3" style={{ color: '#64748b' }}>
                {t('trading.size')} ({inputMode === 'lots' ? 'Lots' : 'USD'})
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder={inputMode === 'lots' ? `Min ${minTradeSize}` : 'Enter USD amount'}
                  value={longAmount}
                  onChange={(e) => {
                    setLongAmount(e.target.value);
                    setLongPercentage(0);
                  }}
                  className="w-full bg-transparent px-3 sm:px-4 py-2.5 sm:py-3 pr-14 sm:pr-16 rounded-xl border focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all font-mono text-sm sm:text-base"
                  style={{ color: '#1e293b', borderColor: 'rgba(0, 0, 0, 0.1)' }}
                  translate="no"
                />
                <span className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-xs sm:text-sm font-medium" style={{ color: '#64748b' }} translate="no">
                  {inputMode === 'lots' ? 'LOTS' : 'USD'}
                </span>
              </div>
              {inputMode === 'usd' && longAmount && longLotsAmount > 0 && (
                <div className="mt-1 text-xs" style={{ color: '#64748b' }}>
                  = {longLotsAmount.toFixed(5)} lots
                </div>
              )}
            </div>

            {/* Percentage Buttons */}
            <div className="flex gap-1 md:gap-2 mb-3">
              {percentageOptions.map((percentage) => (
                <button
                  key={percentage}
                  onClick={() => handleLongPercentage(percentage)}
                  className={`flex-1 py-2 text-xs rounded-lg font-medium transition-all duration-300 ${
                    longPercentage === percentage
                      ? 'text-white shadow-lg transform scale-105'
                      : 'border hover:bg-slate-100'
                  }`}
                  style={longPercentage === percentage ? {
                    backgroundColor: '#d91222',
                    boxShadow: '0 10px 15px -3px rgba(217, 18, 34, 0.25)'
                  } : { background: 'rgba(0, 0, 0, 0.02)', color: '#64748b', borderColor: 'rgba(0, 0, 0, 0.1)' }}
                >
                  {percentage}%
                </button>
              ))}
            </div>

            {/* Stop Loss & Take Profit */}
            {longAmount && longLotsAmount > 0 && (
            <div className="border-t pt-3 md:pt-6 mb-3" style={{ borderColor: 'rgba(0, 0, 0, 0.1)' }}>
              <div className="flex gap-1.5 sm:gap-2">
                <button
                  onClick={() => setShowLongSLModal(true)}
                  className={`flex-1 py-2 sm:py-3 rounded-lg border transition-all text-xs sm:text-sm font-medium ${
                    longStopLoss
                      ? 'bg-red-500/20 border-red-500/50 text-red-600 hover:bg-red-500/30'
                      : 'hover:bg-slate-100'
                  }`}
                  style={!longStopLoss ? { background: 'rgba(0, 0, 0, 0.02)', borderColor: 'rgba(0, 0, 0, 0.1)', color: '#64748b' } : {}}
                >
                  {longStopLoss ? <Edit2 size={12} className="inline mr-1" /> : <Plus size={12} className="inline mr-1" />}
                  <span className="hidden sm:inline">{t('trading.stopLoss')}</span>
                  <span className="sm:hidden">SL</span>
                  {longStopLoss && (
                    <>
                      <span className="ml-1 sm:ml-2 font-mono text-xs sm:text-sm">${longStopLoss.trigger_price.toFixed(2)}</span>
                      <X
                        size={12}
                        className="inline ml-1 sm:ml-2"
                        onClick={(e) => { e.stopPropagation(); setLongStopLoss(null); }}
                      />
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowLongTPModal(true)}
                  className={`flex-1 py-2 sm:py-3 rounded-lg border transition-all text-xs sm:text-sm font-medium ${
                    longTakeProfit
                      ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-600 hover:bg-emerald-500/30'
                      : 'hover:bg-slate-100'
                  }`}
                  style={!longTakeProfit ? { background: 'rgba(0, 0, 0, 0.02)', borderColor: 'rgba(0, 0, 0, 0.1)', color: '#64748b' } : {}}
                >
                  {longTakeProfit ? <Edit2 size={12} className="inline mr-1" /> : <Plus size={12} className="inline mr-1" />}
                  <span className="hidden sm:inline">{t('trading.takeProfit')}</span>
                  <span className="sm:hidden">TP</span>
                  {longTakeProfit && (
                    <>
                      <span className="ml-1 sm:ml-2 font-mono text-xs sm:text-sm">${longTakeProfit.trigger_price.toFixed(2)}</span>
                      <X
                        size={12}
                        className="inline ml-1 sm:ml-2"
                        onClick={(e) => { e.stopPropagation(); setLongTakeProfit(null); }}
                      />
                    </>
                  )}
                </button>
              </div>
            </div>
            )}

            {/* Long Trade Button */}
            <div className="space-y-2 sm:space-y-3">
              <div className="space-y-1 sm:space-y-2">
                <div className="flex justify-between text-xs sm:text-sm" style={{ color: '#64748b' }}>
                  <span>{t('trading.requiredMargin')}</span>
                  <span className="font-mono text-xs sm:text-sm" style={{ color: '#1e293b' }}>{formatRequiredMargin(calculateLongCost())} USD</span>
                </div>
                {longSpreadInfo && (
                  <div className="flex justify-between text-xs" style={{ color: '#94a3b8' }}>
                    <span className="text-xs">Spread ({longSpreadInfo.percentage})</span>
                    <span className="font-mono text-xs" style={{ color: '#f97316' }}>{parseFloat(longSpreadInfo.cost).toFixed(4)} USD</span>
                  </div>
                )}
              </div>
              <button
                onClick={handleLong}
                className="w-full text-white font-bold py-3 sm:py-4 rounded-xl transition-all duration-300 shadow-lg transform hover:scale-105 text-sm sm:text-base"
                style={{
                  backgroundColor: '#d91222',
                  boxShadow: '0 10px 15px -3px rgba(217, 18, 34, 0.25)'
                }}
              >
                {t('trading.long')} {selectedPair}
              </button>
            </div>
          </div>
        </div>

        {/* Short Form */}
        <div className="flex-1">
          <div className="mb-4 sm:mb-6">
            <div className="flex justify-between text-xs sm:text-sm mb-3" style={{ color: '#64748b' }}>
              <span>{t('trading.availableMargin')}</span>
              <span className="flex items-center gap-1.5 sm:gap-2 font-mono text-xs sm:text-sm" style={{ color: '#d91222' }}>
                {(availableBalance !== undefined ? availableBalance : usdtBalance).toFixed(4)} USD
                <div className="w-1 h-1 rounded-full animate-pulse" style={{ backgroundColor: '#d91222' }}></div>
              </span>
            </div>
          </div>

          <div className="space-y-6">
            {/* Price Input */}
            <div className="mb-3">
              <label className="block text-xs sm:text-sm mb-1 md:mb-3" style={{ color: '#64748b' }}>{t('common.price')} (USD)</label>
              <input
                type="text"
                value={livePairPrice.toFixed(getPricePrecision())}
                className="w-full bg-transparent px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all font-mono text-sm sm:text-base"
                style={{ color: '#1e293b', borderColor: 'rgba(0, 0, 0, 0.1)' }}
                readOnly
              />
            </div>

            {/* Amount Input */}
            <div className="mb-3">
              <label className="block text-xs sm:text-sm mb-1 md:mb-3" style={{ color: '#64748b' }}>
                {t('trading.size')} ({inputMode === 'lots' ? 'Lots' : 'USD'})
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder={inputMode === 'lots' ? `Min ${minTradeSize}` : 'Enter USD amount'}
                  value={shortAmount}
                  onChange={(e) => {
                    setShortAmount(e.target.value);
                    setShortPercentage(0);
                  }}
                  className="w-full bg-transparent px-3 sm:px-4 py-2.5 sm:py-3 pr-14 sm:pr-16 rounded-xl border focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all font-mono text-sm sm:text-base"
                  style={{ color: '#1e293b', borderColor: 'rgba(0, 0, 0, 0.1)' }}
                  translate="no"
                />
                <span className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-xs sm:text-sm font-medium" style={{ color: '#64748b' }} translate="no">
                  {inputMode === 'lots' ? 'LOTS' : 'USD'}
                </span>
              </div>
              {inputMode === 'usd' && shortAmount && shortLotsAmount > 0 && (
                <div className="mt-1 text-xs" style={{ color: '#64748b' }}>
                  = {shortLotsAmount.toFixed(5)} lots
                </div>
              )}
            </div>

            {/* Percentage Buttons */}
            <div className="flex gap-1 md:gap-2 mb-3">
              {percentageOptions.map((percentage) => (
                <button
                  key={percentage}
                  onClick={() => handleShortPercentage(percentage)}
                  className={`flex-1 py-2 text-xs rounded-lg font-medium transition-all duration-300 ${
                    shortPercentage === percentage
                      ? 'text-white shadow-lg transform scale-105'
                      : 'border hover:bg-slate-100'
                  }`}
                  style={shortPercentage === percentage ? {
                    backgroundColor: '#d91222',
                    boxShadow: '0 10px 15px -3px rgba(217, 18, 34, 0.25)'
                  } : { background: 'rgba(0, 0, 0, 0.02)', color: '#64748b', borderColor: 'rgba(0, 0, 0, 0.1)' }}
                >
                  {percentage}%
                </button>
              ))}
            </div>

            {/* Stop Loss & Take Profit */}
            {shortAmount && shortLotsAmount > 0 && (
            <div className="border-t pt-3 md:pt-6 mb-3" style={{ borderColor: 'rgba(0, 0, 0, 0.1)' }}>
              <div className="flex gap-1.5 sm:gap-2">
                <button
                  onClick={() => setShowShortSLModal(true)}
                  className={`flex-1 py-2 sm:py-3 rounded-lg border transition-all text-xs sm:text-sm font-medium ${
                    shortStopLoss
                      ? 'bg-red-500/20 border-red-500/50 text-red-600 hover:bg-red-500/30'
                      : 'hover:bg-slate-100'
                  }`}
                  style={!shortStopLoss ? { background: 'rgba(0, 0, 0, 0.02)', borderColor: 'rgba(0, 0, 0, 0.1)', color: '#64748b' } : {}}
                >
                  {shortStopLoss ? <Edit2 size={12} className="inline mr-1" /> : <Plus size={12} className="inline mr-1" />}
                  <span className="hidden sm:inline">{t('trading.stopLoss')}</span>
                  <span className="sm:hidden">SL</span>
                  {shortStopLoss && (
                    <>
                      <span className="ml-1 sm:ml-2 font-mono text-xs sm:text-sm">${shortStopLoss.trigger_price.toFixed(2)}</span>
                      <X
                        size={12}
                        className="inline ml-1 sm:ml-2"
                        onClick={(e) => { e.stopPropagation(); setShortStopLoss(null); }}
                      />
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowShortTPModal(true)}
                  className={`flex-1 py-2 sm:py-3 rounded-lg border transition-all text-xs sm:text-sm font-medium ${
                    shortTakeProfit
                      ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-600 hover:bg-emerald-500/30'
                      : 'hover:bg-slate-100'
                  }`}
                  style={!shortTakeProfit ? { background: 'rgba(0, 0, 0, 0.02)', borderColor: 'rgba(0, 0, 0, 0.1)', color: '#64748b' } : {}}
                >
                  {shortTakeProfit ? <Edit2 size={12} className="inline mr-1" /> : <Plus size={12} className="inline mr-1" />}
                  <span className="hidden sm:inline">{t('trading.takeProfit')}</span>
                  <span className="sm:hidden">TP</span>
                  {shortTakeProfit && (
                    <>
                      <span className="ml-1 sm:ml-2 font-mono text-xs sm:text-sm">${shortTakeProfit.trigger_price.toFixed(2)}</span>
                      <X
                        size={12}
                        className="inline ml-1 sm:ml-2"
                        onClick={(e) => { e.stopPropagation(); setShortTakeProfit(null); }}
                      />
                    </>
                  )}
                </button>
              </div>
            </div>
            )}

            {/* Short Trade Button */}
            <div className="space-y-2 sm:space-y-3">
              <div className="space-y-1 sm:space-y-2">
                <div className="flex justify-between text-xs sm:text-sm" style={{ color: '#64748b' }}>
                  <span>{t('trading.requiredMargin')}</span>
                  <span className="font-mono text-xs sm:text-sm" style={{ color: '#1e293b' }}>{formatRequiredMargin(calculateShortCost())} USD</span>
                </div>
                {shortSpreadInfo && (
                  <div className="flex justify-between text-xs" style={{ color: '#94a3b8' }}>
                    <span className="text-xs">Spread ({shortSpreadInfo.percentage})</span>
                    <span className="font-mono text-xs" style={{ color: '#f97316' }}>{parseFloat(shortSpreadInfo.cost).toFixed(4)} USD</span>
                  </div>
                )}
              </div>
              <button
                onClick={handleShort}
                className="w-full text-white font-bold py-3 sm:py-4 rounded-xl transition-all duration-300 shadow-lg transform hover:scale-105 text-sm sm:text-base"
                style={{
                  backgroundColor: '#d91222',
                  boxShadow: '0 10px 15px -3px rgba(217, 18, 34, 0.25)'
                }}
              >
                {t('trading.short')} {selectedPair}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Take Profit / Stop Loss Modals */}
      <TakeProfitStopLossModal
        isOpen={showLongSLModal}
        onClose={() => setShowLongSLModal(false)}
        type="stopLoss"
        side="long"
        entryPrice={livePairPrice}
        amount={longLotsAmount}
        leverage={leverage}
        lotSize={getLotSize(selectedPair)}
        onConfirm={(price, type, execPrice) => {
          setLongStopLoss({ trigger_price: price, execution_type: type, execution_price: execPrice });
        }}
      />

      <TakeProfitStopLossModal
        isOpen={showLongTPModal}
        onClose={() => setShowLongTPModal(false)}
        type="takeProfit"
        side="long"
        entryPrice={livePairPrice}
        amount={longLotsAmount}
        leverage={leverage}
        lotSize={getLotSize(selectedPair)}
        onConfirm={(price, type, execPrice) => {
          setLongTakeProfit({ trigger_price: price, execution_type: type, execution_price: execPrice });
        }}
      />

      <TakeProfitStopLossModal
        isOpen={showShortSLModal}
        onClose={() => setShowShortSLModal(false)}
        type="stopLoss"
        side="short"
        entryPrice={livePairPrice}
        amount={shortLotsAmount}
        leverage={leverage}
        lotSize={getLotSize(selectedPair)}
        onConfirm={(price, type, execPrice) => {
          setShortStopLoss({ trigger_price: price, execution_type: type, execution_price: execPrice });
        }}
      />

      <TakeProfitStopLossModal
        isOpen={showShortTPModal}
        onClose={() => setShowShortTPModal(false)}
        type="takeProfit"
        side="short"
        entryPrice={livePairPrice}
        amount={shortLotsAmount}
        leverage={leverage}
        lotSize={getLotSize(selectedPair)}
        onConfirm={(price, type, execPrice) => {
          setShortTakeProfit({ trigger_price: price, execution_type: type, execution_price: execPrice });
        }}
      />
    </div>
  );
};

export default CFDTradingForms;
